import { useState, useEffect, useCallback, useMemo, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Phone, Mail, MapPin, Menu, X, ChevronRight, Star, Clock, 
  Truck, Shield, Users, PartyPopper, CheckCircle,
  Send, Plus, Minus, ShoppingBag, LogOut, Eye,
  Package, Trash2, ChevronDown
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Constants
const MIN_ORDER_AMOUNT = 0;
const TAX_RATE = 0.06;
const SERVICE_FEE_RATE = 0.04;
const DISCOUNT_TIER_1 = 500;
const DISCOUNT_RATE_1 = 0.10;

// Business Info
const BUSINESS = {
  name: "Matt's Party Rental",
  phone: "484-935-2207",
  email: "mattpartyrentals@gmail.com",
  location: "Philadelphia, Delaware County, Ardmore PA"
};

// Quote Cart Context
const CartContext = createContext();

export const useCart = () => useContext(CartContext);

const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const addToCart = (product, quantity = 1) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.product.id === product.id 
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { product, quantity }];
    });
    toast.success(`Added ${product.name} to cart!`);
  };

  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.product.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCartItems(prev => 
      prev.map(item => 
        item.product.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => setCartItems([]);

  const getSubtotal = () => 
    cartItems.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);

  const getItemCount = () => 
    cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const contextValue = useMemo(() => ({
    cartItems, addToCart, removeFromCart, updateQuantity, clearCart,
    getSubtotal, getItemCount, isCartOpen, setIsCartOpen
  }), [cartItems, isCartOpen]);

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

// Promo Banner Component - Fixed below navigation
const PromoBanner = () => (
  <div className="fixed top-16 md:top-[100px] left-0 right-0 z-40 promo-banner text-white py-2 px-4 text-center text-sm font-medium" data-testid="promo-banner">
    <span className="inline-flex items-center gap-2">
      <PartyPopper className="w-4 h-4" />
      <span>Summer Special: 10% off orders $500+ with code <strong>SUMMER10</strong></span>
      <PartyPopper className="w-4 h-4" />
    </span>
  </div>
);

// Navigation Component
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { getItemCount, setIsCartOpen } = useCart();
  const itemCount = getItemCount();

  const handleScroll = useCallback(() => setIsScrolled(window.scrollY > 50), []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/products', label: 'Products' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/about', label: 'About' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <>
      {/* Top Contact Bar - Fixed */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-reliable-navy text-white py-2 px-4 hidden md:block" data-testid="contact-bar">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <span className="font-bold">{BUSINESS.name}</span>
            <a href={`tel:${BUSINESS.phone}`} className="flex items-center gap-1 hover:text-celebration-red-light transition-colors">
              <Phone className="w-3.5 h-3.5" />
              {BUSINESS.phone}
            </a>
            <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-1 hover:text-celebration-red-light transition-colors">
              <Mail className="w-3.5 h-3.5" />
              {BUSINESS.email}
            </a>
          </div>
          <div className="flex items-center gap-1 text-gray-300">
            <MapPin className="w-3.5 h-3.5" />
            <span>{BUSINESS.location}</span>
          </div>
        </div>
      </div>

      <nav className={`fixed top-0 md:top-9 left-0 right-0 z-50 transition-colors duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`} data-testid="navigation">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <span className="font-serif text-xl font-bold text-celebration-red">{BUSINESS.name}</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-medium transition-colors ${
                    location.pathname === link.path 
                      ? 'text-celebration-red' 
                      : 'text-gray-700 hover:text-celebration-red'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA + Cart */}
            <div className="hidden lg:flex items-center gap-4">
              <button 
                onClick={() => setIsCartOpen(true)}
                className="relative p-2 bg-celebration-red text-white rounded-full hover:bg-celebration-red-dark transition-colors"
                data-testid="cart-button"
              >
                <ShoppingBag className="w-6 h-6" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-reliable-navy text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {itemCount}
                  </span>
                )}
              </button>
              <a href={`tel:${BUSINESS.phone}`} className="btn-primary flex items-center gap-2" data-testid="call-cta">
                <Phone className="w-4 h-4" />
                <span>Call Now</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex lg:hidden items-center gap-3">
            <button 
              onClick={() => setIsCartOpen(true)}
              className="relative p-2 bg-celebration-red text-white rounded-full"
              data-testid="mobile-cart-button"
            >
              <ShoppingBag className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-reliable-navy text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {itemCount}
                </span>
              )}
            </button>
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-gray-700"
              data-testid="mobile-menu-button"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t py-4" data-testid="mobile-menu">
            {/* Mobile contact info */}
            <div className="px-4 pb-4 mb-2 border-b space-y-2 text-sm">
              <a href={`tel:${BUSINESS.phone}`} className="flex items-center gap-2 text-celebration-red font-bold">
                <Phone className="w-4 h-4" />
                {BUSINESS.phone}
              </a>
              <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-2 text-gray-600">
                <Mail className="w-4 h-4" />
                {BUSINESS.email}
              </a>
            </div>
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block py-3 px-4 font-medium ${
                  location.pathname === link.path 
                    ? 'text-celebration-red bg-red-50' 
                    : 'text-gray-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a 
              href={`tel:${BUSINESS.phone}`} 
              className="block mt-4 mx-4 btn-primary text-center"
            >
              <Phone className="w-4 h-4 inline mr-2" />
              Call Now
            </a>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};

// Cart Sidebar
const CartSidebar = () => {
  const { cartItems, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, getSubtotal, clearCart } = useCart();
  const [showOrderForm, setShowOrderForm] = useState(false);

  if (!isCartOpen) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="cart-sidebar">
      <div className="absolute inset-0 bg-black/50" onClick={() => setIsCartOpen(false)} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-xl overflow-hidden flex flex-col">
        <div className="p-6 border-b flex items-center justify-between bg-gray-50">
          <h2 className="font-serif text-2xl">Your Cart</h2>
          <button onClick={() => setIsCartOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!showOrderForm ? (
          <>
            <div className="flex-1 overflow-y-auto p-6">
              {cartItems.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ShoppingBag className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Your cart is empty</p>
                  <p className="text-sm mt-2">Add items from our products page</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cartItems.map(({ product, quantity }) => (
                    <div key={product.id} className="flex gap-4 p-4 bg-gray-50 rounded-xl" data-testid={`cart-item-${product.id}`}>
                      <img src={product.image_url} alt={product.name} className="w-20 h-20 object-cover rounded-lg" />
                      <div className="flex-1">
                        <h4 className="font-medium">{product.name}</h4>
                        <p className="text-celebration-red font-bold">${product.price.toFixed(2)}/{product.price_unit}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button 
                            onClick={() => updateQuantity(product.id, quantity - 1)}
                            className="w-8 h-8 flex items-center justify-center border rounded-full hover:bg-gray-100"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center font-medium">{quantity}</span>
                          <button 
                            onClick={() => updateQuantity(product.id, quantity + 1)}
                            className="w-8 h-8 flex items-center justify-center border rounded-full hover:bg-gray-100"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <button 
                        onClick={() => removeFromCart(product.id)}
                        className="text-gray-400 hover:text-celebration-red"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cartItems.length > 0 && (
              <div className="p-6 border-t bg-gray-50">
                <div className="flex justify-between text-lg font-medium mb-2">
                  <span>Subtotal:</span>
                  <span className="text-celebration-red">${getSubtotal().toFixed(2)}</span>
                </div>
                {(() => {
                  const nonPkgTotal = cartItems
                    .filter(({ product }) => product.category !== 'packages' && !product.name.toLowerCase().includes('package') && !product.name.toLowerCase().includes('deal'))
                    .reduce((sum, { product, quantity }) => sum + (product.price * quantity), 0);
                  if (nonPkgTotal >= DISCOUNT_TIER_1) return <p className="text-sm text-gray-500 mb-4">Use code SUMMER10 for 10% off!</p>;
                  if (nonPkgTotal > 0) return <p className="text-sm text-gray-500 mb-4">Use code SUMMER10 for 10% off orders $500+</p>;
                  return <p className="text-sm text-gray-500 mb-4">Packages are already discounted!</p>;
                })()}
                {getSubtotal() < MIN_ORDER_AMOUNT && (
                  <div className="p-3 bg-red-100 rounded-lg border border-red-300 mb-3">
                    <p className="text-sm text-red-700 font-bold">Minimum order is $150</p>
                    <p className="text-xs text-red-600">Add ${(MIN_ORDER_AMOUNT - getSubtotal()).toFixed(2)} more to checkout</p>
                  </div>
                )}
                <button 
                  onClick={() => setShowOrderForm(true)}
                  className={`w-full btn-primary ${getSubtotal() < MIN_ORDER_AMOUNT ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={getSubtotal() < MIN_ORDER_AMOUNT}
                  data-testid="checkout-button"
                >
                  {getSubtotal() < MIN_ORDER_AMOUNT ? `Minimum $150 Order` : 'Checkout'}
                </button>
                <button 
                  onClick={clearCart}
                  className="w-full mt-2 text-gray-500 text-sm hover:text-celebration-red"
                >
                  Clear All
                </button>
              </div>
            )}
          </>
        ) : (
          <OrderForm 
            cartItems={cartItems} 
            subtotal={getSubtotal()} 
            onBack={() => setShowOrderForm(false)}
            onSuccess={() => {
              clearCart();
              setShowOrderForm(false);
              setIsCartOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Order Form Component
const OrderForm = ({ cartItems, subtotal, onBack, onSuccess }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    phone: '',
    email: '',
    event_address: '',
    event_date: '',
    event_time: '',
    setup_surface: '',
    promo_code: '',
    special_requests: ''
  });
  const [loading, setLoading] = useState(false);

  const isPackageItem = (product) => 
    product.category === 'packages' || product.name.toLowerCase().includes('package') || product.name.toLowerCase().includes('deal');

  const getNonPackageSubtotal = () => 
    cartItems.filter(({ product }) => !isPackageItem(product))
      .reduce((sum, { product, quantity }) => sum + (product.price * quantity), 0);

  const getDiscount = () => {
    const nonPkgTotal = getNonPackageSubtotal();
    if (nonPkgTotal <= 0) return 0;
    
    const code = formData.promo_code.toUpperCase().trim();
    if (code === 'SUMMER10' && nonPkgTotal >= DISCOUNT_TIER_1) return nonPkgTotal * DISCOUNT_RATE_1;
    return 0;
  };

  const getDiscountLabel = () => {
    if (formData.promo_code.toUpperCase().trim() === 'SUMMER10') return 'SUMMER10';
    return '10%';
  };

  const getSubmitLabel = () => {
    if (loading) return 'Submitting...';
    if (subtotal < MIN_ORDER_AMOUNT) return 'Minimum $150 Order Required';
    return 'Submit Order';
  };

  const afterDiscount = subtotal - getDiscount();
  const tax = Math.round(afterDiscount * TAX_RATE * 100) / 100;
  const serviceFee = Math.round(afterDiscount * SERVICE_FEE_RATE * 100) / 100;
  const total = Math.round((afterDiscount + tax + serviceFee) * 100) / 100;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (subtotal < MIN_ORDER_AMOUNT) {
      toast.error("Minimum order is $150. Please add more items.");
      return;
    }
    setLoading(true);

    try {
      const items = cartItems.map(({ product, quantity }) => ({
        product_id: product.id,
        product_name: product.name,
        quantity,
        price: product.price
      }));

      await axios.post(`${API}/quotes`, {
        ...formData,
        items
      });

      toast.success("Order submitted! Email mattpartyrentals@gmail.com or call 484-935-2207 to confirm your order.");
      onSuccess();
    } catch (error) {
      toast.error("Failed to submit order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6" data-testid="order-form">
      <button 
        type="button" 
        onClick={onBack}
        className="flex items-center gap-1 text-gray-600 hover:text-celebration-red mb-6"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Back to cart
      </button>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
          <input
            type="text"
            required
            value={formData.customer_name}
            onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
            className="w-full p-3 border rounded-lg bg-gray-50"
            data-testid="order-name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
          <input
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full p-3 border rounded-lg bg-gray-50"
            data-testid="order-phone"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full p-3 border rounded-lg bg-gray-50"
            placeholder="For invoice delivery (optional)"
            data-testid="order-email"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Address *</label>
          <textarea
            required
            value={formData.event_address}
            onChange={(e) => setFormData({ ...formData, event_address: e.target.value })}
            className="w-full p-3 border rounded-lg bg-gray-50"
            rows={2}
            placeholder="Street address, city, state, zip"
            data-testid="order-address"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Date *</label>
            <input
              type="date"
              required
              value={formData.event_date}
              onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
              className="w-full p-3 border rounded-lg bg-gray-50"
              data-testid="order-date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Time *</label>
            <input
              type="time"
              required
              value={formData.event_time}
              onChange={(e) => setFormData({ ...formData, event_time: e.target.value })}
              className="w-full p-3 border rounded-lg bg-gray-50"
              data-testid="order-time"
            />
          </div>
        </div>

        {cartItems.some(({ product }) => 
          product.category === 'tents' || product.category === 'packages'
        ) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Setup Surface *</label>
            <select
              required
              value={formData.setup_surface}
              onChange={(e) => setFormData({ ...formData, setup_surface: e.target.value })}
              className="w-full p-3 border rounded-lg bg-gray-50"
              data-testid="order-surface"
            >
              <option value="">Select setup surface</option>
              <option value="grass">Grass</option>
              <option value="pavement">Pavement / Concrete</option>
              <option value="mixed">Mixed (Grass & Pavement)</option>
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Promo Code</label>
          <input
            type="text"
            value={formData.promo_code}
            onChange={(e) => setFormData({ ...formData, promo_code: e.target.value })}
            placeholder="Enter code"
            className="w-full p-3 border rounded-lg bg-gray-50"
            data-testid="order-promo"
          />
          <p className="text-xs text-gray-400 mt-1">One discount per customer</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests</label>
          <textarea
            value={formData.special_requests}
            onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
            className="w-full p-3 border rounded-lg bg-gray-50"
            rows={2}
            placeholder="Setup instructions, timing preferences, etc."
            data-testid="order-requests"
          />
        </div>
      </div>

      <div className="mt-6 p-4 bg-red-50 rounded-xl space-y-2">
        {subtotal < MIN_ORDER_AMOUNT && (
          <div className="p-2 bg-red-100 rounded-lg border border-red-300 mb-2">
            <p className="text-xs text-red-700 font-medium">Minimum order is $150. Add ${(MIN_ORDER_AMOUNT - subtotal).toFixed(2)} more.</p>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span>Subtotal:</span>
          <span>${subtotal.toFixed(2)}</span>
        </div>
        {getDiscount() > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>Discount ({getDiscountLabel()}):</span>
            <span>-${getDiscount().toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span>Tax (6%):</span>
          <span>${tax.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>Service Fee (4%):</span>
          <span>${serviceFee.toFixed(2)}</span>
        </div>
        <div className="p-2 bg-yellow-50 rounded-lg border border-yellow-200 my-2">
          <p className="text-xs text-yellow-700 font-medium">Delivery fee varies by location and will be added to your final invoice</p>
        </div>
        <div className="flex justify-between font-bold text-lg pt-2 border-t border-red-200">
          <span>Total:</span>
          <span className="text-celebration-red">${total.toFixed(2)}</span>
        </div>
      </div>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
        <p className="text-xs text-blue-700">Email <strong>mattpartyrentals@gmail.com</strong> or call <strong>484-935-2207</strong> to place your order. Invoice sent within 24 hours. 50% deposit required, balance due on delivery.</p>
      </div>

      <button 
        type="submit"
        disabled={loading || subtotal < MIN_ORDER_AMOUNT}
        className="w-full mt-4 btn-primary disabled:opacity-50"
        data-testid="submit-order"
      >
        {getSubmitLabel()}
      </button>
    </form>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-reliable-navy text-white" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
        {/* Brand */}
        <div>
          <h3 className="font-serif text-2xl text-white mb-4">{BUSINESS.name}</h3>
          <p className="text-gray-300 text-sm mb-4">
            Your trusted party rental partner in Philadelphia and Delaware County. 
            Making your events memorable since day one.
          </p>
          <div className="flex flex-col gap-2">
            <a href={`tel:${BUSINESS.phone}`} className="text-celebration-red-light font-bold text-lg hover:text-white transition-colors">
              {BUSINESS.phone}
            </a>
            <a href={`mailto:${BUSINESS.email}`} className="text-gray-300 hover:text-white transition-colors text-sm">
              {BUSINESS.email}
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-serif text-xl mb-4">Quick Links</h4>
          <div className="space-y-2">
            {['Products', 'Gallery', 'About', 'FAQ', 'Contact'].map(link => (
              <Link 
                key={link}
                to={`/${link.toLowerCase()}`}
                className="block text-gray-300 hover:text-white transition-colors"
              >
                {link}
              </Link>
            ))}
          </div>
        </div>

        {/* Products */}
        <div>
          <h4 className="font-serif text-xl mb-4">Products</h4>
          <div className="space-y-2 text-gray-300">
            <p>Chairs</p>
            <p>Tables</p>
            <p>Tents</p>
          </div>
        </div>

        {/* Contact */}
        <div>
          <h4 className="font-serif text-xl mb-4">Contact Us</h4>
          <div className="space-y-3 text-gray-300">
            <a href={`tel:${BUSINESS.phone}`} className="flex items-center gap-2 hover:text-white">
              <Phone className="w-4 h-4" />
              {BUSINESS.phone}
            </a>
            <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-2 hover:text-white">
              <Mail className="w-4 h-4" />
              {BUSINESS.email}
            </a>
            <p className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
              {BUSINESS.location}
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-700 mt-12 pt-8 text-center text-gray-400 text-sm">
        <p>&copy; {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Home Page
const HomePage = () => {
  const [products, setProducts] = useState([]);
  const { addToCart } = useCart();

  useEffect(() => {
    axios.get(`${API}/products`)
      .then(res => setProducts(res.data.slice(0, 6)))
      .catch(() => {});
  }, []);

  const features = [
    { icon: Truck, title: "Delivery Available", desc: "Delivery & pickup based on location" },
    { icon: Clock, title: "On-Time Setup", desc: "Always on time, every time" },
    { icon: Shield, title: "Quality Guaranteed", desc: "Clean, well-maintained equipment" },
    { icon: Users, title: "Expert Team", desc: "Professional setup by experienced crew" }
  ];

  const testimonials = [
    { name: "Sarah M.", event: "Wedding", text: "Matt's team made our wedding day perfect! The tents were gorgeous and the setup was flawless." },
    { name: "John D.", event: "Graduation Party", text: "Best rental experience ever. Great prices, amazing service, and everything was delivered on time." },
    { name: "Lisa R.", event: "Corporate Event", text: "Professional, reliable, and affordable. Will definitely use again for all our company events." }
  ];

  return (
    <main data-testid="home-page">
      {/* Hero Section - Graduation Focus */}
      <section className="relative min-h-[600px] flex items-center bg-reliable-navy" data-testid="hero-section">
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8 w-full py-32">
          <div className="text-center">
            <p className="text-celebration-red-light text-lg font-medium mb-4 animate-fade-in-up">CONGRATS GRAD!</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6 animate-fade-in-up">
              Tent Party Packages
            </h1>
            <p className="text-lg text-gray-300 mb-4 max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Everything you need for the perfect celebration - tent, tables, chairs & more!
            </p>
            <p className="text-celebration-red-light font-bold text-lg animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              Setup & breakdown included!
            </p>
          </div>
        </div>
      </section>

      {/* Graduation Packages */}
      <section className="section-padding bg-gray-50" data-testid="packages-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Tent Starter */}
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-shadow relative">
              <div className="bg-celebration-red text-white text-center py-2 text-sm font-bold">STARTER</div>
              <div className="p-6 text-center">
                <h3 className="font-serif text-2xl mb-2">Tent Starter</h3>
                <p className="text-4xl font-bold text-celebration-red mb-1">$220</p>
                <p className="text-gray-500 text-sm mb-4">10x20 Setup</p>
                <ul className="text-left space-y-2 mb-6 text-sm">
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 10x20 Tent</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 3 48&quot; Round Tables (seats 5 each)</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 15 Basic Chairs</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Setup & Breakdown Included</li>
                  <li className="flex items-start gap-2 text-gray-400 text-xs"><span>* Delivery not included</span></li>
                </ul>
                <button onClick={() => { addToCart({ id: 'pkg-starter', name: 'Tent Starter 10x20', price: 220, price_unit: 'package', category: 'packages', image_url: '' }); }} className="w-full btn-primary" data-testid="add-pkg-starter">
                  Add to Cart - $220
                </button>
              </div>
            </div>

            {/* Tent Package $590 */}
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-shadow relative">
              <div className="bg-reliable-navy text-white text-center py-2 text-sm font-bold">POPULAR</div>
              <div className="p-6 text-center">
                <h3 className="font-serif text-2xl mb-2">Tent Package</h3>
                <p className="text-4xl font-bold text-celebration-red mb-1">$590</p>
                <p className="text-gray-500 text-sm mb-4">20x20 Setup</p>
                <ul className="text-left space-y-2 mb-6 text-sm">
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 20x20 Tent</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 4 Round OR Rectangle Tables</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 40 Basic Chairs</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 2 Cocktail Tables</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Setup & Breakdown Included</li>
                  <li className="flex items-start gap-2 text-gray-400 text-xs"><span>* Delivery not included</span></li>
                </ul>
                <button onClick={() => { addToCart({ id: 'pkg-tent-20x20', name: 'Tent Package 20x20', price: 590, price_unit: 'package', category: 'packages', image_url: '' }); }} className="w-full btn-primary" data-testid="add-pkg-590">
                  Add to Cart - $590
                </button>
              </div>
            </div>

            {/* Tent Package Chiavari $899 */}
            <div className="bg-white rounded-2xl overflow-hidden border-2 border-celebration-red shadow-lg hover:shadow-xl transition-shadow relative">
              <div className="bg-celebration-red text-white text-center py-2 text-sm font-bold">PREMIUM</div>
              <div className="overflow-hidden h-48">
                <img src="https://customer-assets.emergentagent.com/job_c5ed34de-4333-4910-9f9e-3c7656648479/artifacts/fxt1hdmn_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2008_03_45%20AM.png" alt="Tent Package Chiavari" className="w-full h-full object-cover" />
              </div>
              <div className="p-6 text-center">
                <h3 className="font-serif text-2xl mb-2">Tent Chiavari</h3>
                <p className="text-4xl font-bold text-celebration-red mb-1">$899</p>
                <p className="text-gray-500 text-sm mb-4">20x20 Premium Setup</p>
                <ul className="text-left space-y-2 mb-6 text-sm">
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 20x20 Frame Tent</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 4 Round Tables</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 32 Gold or Clear Chiavari Chairs</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> White or Black Linens</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 2 Cocktail Tables w/ Spandex Covers</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Lights Included</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Setup & Breakdown Included</li>
                  <li className="flex items-start gap-2 text-gray-400 text-xs"><span>* Delivery not included</span></li>
                </ul>
                <button onClick={() => { addToCart({ id: 'pkg-chiavari', name: 'Tent Package 20x20 Chiavari', price: 899, price_unit: 'package', category: 'packages', image_url: '' }); }} className="w-full btn-primary" data-testid="add-pkg-chiavari">
                  Add to Cart - $899
                </button>
              </div>
            </div>

            {/* Tent Deluxe $670 */}
            <div className="bg-white rounded-2xl overflow-hidden border-2 border-yellow-500 shadow-lg hover:shadow-xl transition-shadow relative">
              <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-center py-2 text-sm font-bold">DELUXE</div>
              <div className="overflow-hidden h-48">
                <img src="https://customer-assets.emergentagent.com/job_c5ed34de-4333-4910-9f9e-3c7656648479/artifacts/nybeu5xv_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2008_14_38%20AM.png" alt="Tent Deluxe Package" className="w-full h-full object-cover" />
              </div>
              <div className="p-6 text-center">
                <h3 className="font-serif text-2xl mb-2">Tent Deluxe</h3>
                <p className="text-4xl font-bold text-celebration-red mb-1">$670</p>
                <p className="text-gray-500 text-sm mb-4">20x20 Premium Setup</p>
                <ul className="text-left space-y-2 mb-6 text-sm">
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 20x20 Tent with Lights</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 40 Padded Chairs</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 4 Round Tables</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> 2 Cocktail Tables</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Black or White Linens</li>
                  <li className="flex items-start gap-2 text-gray-700"><CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> Setup & Breakdown Included</li>
                  <li className="flex items-start gap-2 text-gray-400 text-xs"><span>* Delivery not included. Other color linens extra.</span></li>
                </ul>
                <button onClick={() => { addToCart({ id: 'pkg-deluxe', name: 'Tent Deluxe Package 20x20', price: 670, price_unit: 'package', category: 'packages', image_url: '' }); }} className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-6 py-3 rounded-full font-bold hover:from-yellow-600 hover:to-yellow-700 transition-all" data-testid="add-pkg-deluxe">
                  Add to Cart - $670
                </button>
              </div>
            </div>
          </div>
          <p className="text-center text-gray-500 text-sm mt-6">Already discounted! No promo code needed. * Delivery not included. Fee based on location. Book early - dates fill up fast!</p>
          <div className="text-center mt-6 flex flex-wrap justify-center gap-4">
            <Link to="/products" className="btn-primary" data-testid="view-all-rentals">View All Rentals</Link>
            <a href={`tel:${BUSINESS.phone}`} className="btn-secondary">{BUSINESS.phone}</a>
          </div>
        </div>
      </section>

      {/* Table & Chair Deals */}
      <section className="section-padding" data-testid="deals-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl md:text-4xl mb-3">Table & Chair Deals</h2>
            <p className="text-gray-600">Perfect for smaller gatherings! Get a complete table setup at unbeatable prices.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-shadow">
              <div className="aspect-video overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_38c29b8d-25e8-4057-93bb-daa48e9e96ee/artifacts/28pjo8bw_ChatGPT%20Image%20Mar%202%2C%202026%2C%2007_36_39%20PM.png" alt="$28 Basic Chair & Round Table Package" className="w-full h-full object-cover" />
              </div>
              <div className="p-6 text-center">
                <h3 className="font-serif text-xl mb-3">$28 Basic Chair & Round Table Package</h3>
                <button onClick={() => { addToCart({ id: 'deal-28', name: '$28 Basic Chair & Round Table Package', price: 28, price_unit: 'package', category: 'packages', image_url: 'https://customer-assets.emergentagent.com/job_38c29b8d-25e8-4057-93bb-daa48e9e96ee/artifacts/28pjo8bw_ChatGPT%20Image%20Mar%202%2C%202026%2C%2007_36_39%20PM.png' }); }} className="btn-primary" data-testid="add-deal-28">
                  Add to Cart - $28
                </button>
              </div>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-shadow">
              <div className="aspect-video overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_38c29b8d-25e8-4057-93bb-daa48e9e96ee/artifacts/aaw79wt0_ChatGPT%20Image%20Mar%202%2C%202026%2C%2007_38_57%20PM.png" alt="$77 Table & Chair Deal" className="w-full h-full object-cover" />
              </div>
              <div className="p-6 text-center">
                <h3 className="font-serif text-xl mb-3">$77 Table & Chair Deal</h3>
                <button onClick={() => { addToCart({ id: 'deal-77', name: '$77 Table & Chair Deal', price: 77, price_unit: 'package', category: 'packages', image_url: 'https://customer-assets.emergentagent.com/job_38c29b8d-25e8-4057-93bb-daa48e9e96ee/artifacts/aaw79wt0_ChatGPT%20Image%20Mar%202%2C%202026%2C%2007_38_57%20PM.png' }); }} className="btn-primary" data-testid="add-deal-77">
                  Add to Cart - $77
                </button>
              </div>
            </div>
          </div>
          <p className="text-center text-gray-400 text-sm mt-4">* Delivery not included. These deals are already discounted!</p>
        </div>
      </section>

      {/* Throne Chair Specials */}
      <section className="section-padding bg-gray-50" data-testid="throne-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl md:text-4xl mb-3">Throne Chair Specials</h2>
            <p className="text-gray-600">Make your celebration royal! Rent the pair and save on our luxurious throne chairs.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-shadow">
              <div className="aspect-square overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_38c29b8d-25e8-4057-93bb-daa48e9e96ee/artifacts/i37sy0tl_ChatGPT%20Image%20Mar%202%2C%202026%2C%2009_13_05%20PM.png" alt="Throne Chair Pair" className="w-full h-full object-cover" />
              </div>
              <div className="p-6 text-center">
                <h3 className="font-serif text-xl mb-3">Throne Chair Pair - $260</h3>
                <button onClick={() => { addToCart({ id: 'throne-pair', name: 'Throne Chair Pair', price: 260, price_unit: 'per day', category: 'chairs', image_url: 'https://customer-assets.emergentagent.com/job_38c29b8d-25e8-4057-93bb-daa48e9e96ee/artifacts/i37sy0tl_ChatGPT%20Image%20Mar%202%2C%202026%2C%2009_13_05%20PM.png' }); }} className="btn-primary" data-testid="add-throne-pair">
                  Add to Cart - $260
                </button>
              </div>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-shadow">
              <div className="aspect-square overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_38c29b8d-25e8-4057-93bb-daa48e9e96ee/artifacts/bbd7s3iw_tempFileForShare_20260302-210218.jpg" alt="O Chair Gold Pair" className="w-full h-full object-cover" />
              </div>
              <div className="p-6 text-center">
                <h3 className="font-serif text-xl mb-3">O Chair Gold Pair - $220</h3>
                <button onClick={() => { addToCart({ id: 'o-chair-pair', name: 'O Chair Gold Pair', price: 220, price_unit: 'per day', category: 'chairs', image_url: 'https://customer-assets.emergentagent.com/job_38c29b8d-25e8-4057-93bb-daa48e9e96ee/artifacts/bbd7s3iw_tempFileForShare_20260302-210218.jpg' }); }} className="btn-primary" data-testid="add-o-pair">
                  Add to Cart - $220
                </button>
              </div>
            </div>
          </div>
          <p className="text-center text-gray-400 text-sm mt-4">* Delivery not included. Perfect for quinceañeras, sweet 16s, baby showers & more!</p>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-gray-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <div 
                key={feature.title} 
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow border border-gray-100"
              >
                <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-celebration-red" />
                </div>
                <h3 className="font-serif text-xl mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section-padding" data-testid="products-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl md:text-5xl mb-4">Our Rentals</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Quality equipment for every occasion. From intimate gatherings to grand celebrations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map(product => (
              <div key={product.id} className="product-card bg-white rounded-xl overflow-hidden border border-gray-100" data-testid={`product-${product.id}`}>
                <div className="aspect-square overflow-hidden bg-gray-100">
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs uppercase tracking-wider text-gray-500">{product.category}</span>
                  <h3 className="font-serif text-xl mt-1">{product.name}</h3>
                  <p className="text-gray-600 text-sm mt-2 line-clamp-2 whitespace-pre-line">{product.description}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-celebration-red font-bold text-lg">
                      ${product.price.toFixed(2)} <span className="text-sm font-normal text-gray-500">/{product.price_unit}</span>
                    </span>
                    <button 
                      onClick={() => addToCart(product)}
                      className="btn-secondary px-4 py-2 text-sm"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/products" className="btn-primary" data-testid="view-all-products">
              View All Products
              <ChevronRight className="w-5 h-5 inline ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-celebration-red py-16 px-4" data-testid="cta-banner">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="font-serif text-3xl md:text-4xl mb-4">Ready to Plan Your Event?</h2>
          <p className="text-red-100 mb-8 text-lg">
            Get a free quote today and save up to 15% on your order!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/products" className="bg-white text-celebration-red px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors">
              Browse Products
            </Link>
            <a href={`tel:${BUSINESS.phone}`} className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-celebration-red transition-colors">
              Call {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-gray-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl md:text-5xl mb-4">What Our Customers Say</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white p-8 rounded-2xl shadow-sm">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={`star-${i}`} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">&ldquo;{t.text}&rdquo;</p>
                <div>
                  <p className="font-medium">{t.name}</p>
                  <p className="text-sm text-gray-500">{t.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

// Products Page
const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { addToCart } = useCart();

  useEffect(() => {
    axios.get(`${API}/products`)
      .then(res => setProducts(res.data))
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoading(false));
  }, []);

  const filteredProducts = filter === 'all' 
    ? products 
    : products.filter(p => p.category === filter);

  return (
    <main className="pt-28 md:pt-36" data-testid="products-page">
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-serif text-4xl md:text-5xl mb-4">Our Products</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Quality rental equipment for weddings, graduations, corporate events, and more.
          </p>
        </div>
      </section>

      <CategoryFilter categories={PRODUCT_CATEGORIES} activeFilter={filter} onFilterChange={setFilter} />

      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-celebration-red border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map(product => (
                <ProductCard key={product.id} product={product} onAddToCart={addToCart} />
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

// Gallery Page
const GalleryPage = () => {
  const galleryImages = [
    { url: "https://customer-assets.emergentagent.com/job_c5ed34de-4333-4910-9f9e-3c7656648479/artifacts/4rluakiz_ChatGPT%20Image%20Apr%2016%2C%202026%2C%2006_28_51%20PM.png", caption: "Congratulations Graduate Class of 2026 - Balloon Garland Backdrop" },
    { url: "https://customer-assets.emergentagent.com/job_c5ed34de-4333-4910-9f9e-3c7656648479/artifacts/avkvjyf7_ChatGPT%20Image%20Apr%2016%2C%202026%2C%2006_15_38%20PM.png", caption: "Navy Blue & Gold Table Setup Under Tent with String Lights" },
    { url: "https://customer-assets.emergentagent.com/job_c5ed34de-4333-4910-9f9e-3c7656648479/artifacts/5u3d2cz8_ChatGPT%20Image%20Apr%2016%2C%202026%2C%2006_09_46%20PM.png", caption: "Elegant White & Gold Table Setup Under Tent with Lights" },
    { url: "https://customer-assets.emergentagent.com/job_c5ed34de-4333-4910-9f9e-3c7656648479/artifacts/entma61x_IMG-20250329-WA0012.jpg", caption: "Black Bounce House with Balloon Garland Decor" },
    { url: "https://customer-assets.emergentagent.com/job_1f8ab967-3db0-49ec-9b80-5202cd4760f2/artifacts/83go7vnz_1000039210.jpg", caption: "40th Birthday Celebration - Black & Gold Balloon Decor Philadelphia PA" },
    { url: "https://customer-assets.emergentagent.com/job_1f8ab967-3db0-49ec-9b80-5202cd4760f2/artifacts/meybm8xp_1000159913.jpg", caption: "Gender Reveal Baby Shower Setup Delaware County" },
    { url: "https://customer-assets.emergentagent.com/job_1f8ab967-3db0-49ec-9b80-5202cd4760f2/artifacts/q2gu9ml1_1000156027.jpg", caption: "Outdoor Wedding Ceremony White Folding Chairs Philadelphia" },
    { url: "https://customer-assets.emergentagent.com/job_1f8ab967-3db0-49ec-9b80-5202cd4760f2/artifacts/bwshmmf2_1000168460.jpg", caption: "30th Birthday Party Marquee Numbers Balloon Garland" },
    { url: "https://customer-assets.emergentagent.com/job_1f8ab967-3db0-49ec-9b80-5202cd4760f2/artifacts/ioxannbh_1000159919%20%281%29.jpg", caption: "Baby in Bloom Shower - Pink Balloon Decor Ardmore PA" },
    { url: "https://customer-assets.emergentagent.com/job_1f8ab967-3db0-49ec-9b80-5202cd4760f2/artifacts/qa6inkuf_1000156175%20%281%29.jpg", caption: "Outdoor Wedding Reception Round Tables White Chairs Philadelphia" },
    { url: "https://customer-assets.emergentagent.com/job_1f8ab967-3db0-49ec-9b80-5202cd4760f2/artifacts/rek2j3xa_1000156027%20%281%29.jpg", caption: "Wedding Ceremony Seating White Resin Chairs Delaware County" },
    { url: "https://customer-assets.emergentagent.com/job_1f8ab967-3db0-49ec-9b80-5202cd4760f2/artifacts/ihdlvf2f_1000091624.jpg", caption: "Blue Birthday Party Balloon Arch Backdrop Philadelphia PA" }
  ];

  return (
    <main className="pt-28 md:pt-36" data-testid="gallery-page">
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-serif text-4xl md:text-5xl mb-4">Our Gallery</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Browse through our past events and get inspired for your next celebration.
          </p>
        </div>
      </section>

      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="masonry-grid">
            {galleryImages.map((img) => (
              <div 
                key={img.url} 
                className="masonry-item group relative overflow-hidden rounded-xl"
                data-testid={`gallery-image-${img.caption.slice(0,20)}`}
              >
                <img 
                  src={img.url} 
                  alt={img.caption}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <p className="text-white p-4 font-medium">{img.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-celebration-red py-16 px-4">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="font-serif text-3xl mb-4">Ready to Create Your Own Memories?</h2>
          <Link to="/products" className="inline-block bg-white text-celebration-red px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors">
            Browse Our Products
          </Link>
        </div>
      </section>
    </main>
  );
};

// About Page
const AboutPage = () => (
  <main className="pt-28 md:pt-36" data-testid="about-page">
    <section className="bg-gray-50 py-16 px-4">
      <div className="max-w-7xl mx-auto text-center">
        <h1 className="font-serif text-4xl md:text-5xl mb-4">About Us</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Your trusted party rental partner in Philadelphia and Delaware County.
        </p>
      </div>
    </section>

    <section className="section-padding">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl mb-6">Making Events Memorable</h2>
            <p className="text-gray-600 mb-4">
              At Matt&apos;s Party Rental, we believe every celebration deserves the perfect setup. 
              Based in Ardmore, PA, we proudly serve Philadelphia, Delaware County, and surrounding areas 
              with premium party rental equipment.
            </p>
            <p className="text-gray-600 mb-4">
              Whether you&apos;re planning an intimate backyard gathering or a grand wedding reception, 
              we have the chairs, tables, and tents to make your vision a reality. Our commitment 
              to quality and service means you can focus on enjoying your event while we handle the rest.
            </p>
            <p className="text-gray-600 mb-6">
              With competitive pricing and professional setup included, Matt&apos;s Party Rental is your 
              one-stop shop for all your event needs.
            </p>

            <div className="flex flex-wrap gap-4">
              <a href={`tel:${BUSINESS.phone}`} className="btn-primary" data-testid="about-call">
                <Phone className="w-4 h-4 inline mr-2" />
                Call Us
              </a>
              <Link to="/contact" className="btn-secondary" data-testid="about-contact">
                Get in Touch
              </Link>
            </div>
          </div>

          <div className="relative">
            <img 
              src="https://images.unsplash.com/photo-1768179123386-a86a85f1c35c?w=800"
              alt="Event setup"
              className="rounded-2xl shadow-xl"
            />
            <div className="absolute -bottom-6 -left-6 bg-celebration-red text-white p-6 rounded-xl shadow-lg">
              <p className="font-serif text-4xl">100%</p>
              <p className="text-sm">Customer Satisfaction</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="section-padding bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-serif text-3xl text-center mb-12">Service Area</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-2xl text-center">
            <MapPin className="w-12 h-12 text-celebration-red mx-auto mb-4" />
            <h3 className="font-serif text-xl mb-2">Philadelphia</h3>
            <p className="text-gray-600">All neighborhoods</p>
          </div>
          <div className="bg-white p-8 rounded-2xl text-center">
            <MapPin className="w-12 h-12 text-celebration-red mx-auto mb-4" />
            <h3 className="font-serif text-xl mb-2">Delaware County</h3>
            <p className="text-gray-600">Including Ardmore, Media, Wayne</p>
          </div>
          <div className="bg-white p-8 rounded-2xl text-center">
            <MapPin className="w-12 h-12 text-celebration-red mx-auto mb-4" />
            <h3 className="font-serif text-xl mb-2">Surrounding Areas</h3>
            <p className="text-gray-600">Montgomery & Chester Counties</p>
          </div>
        </div>
      </div>
    </section>
  </main>
);

// FAQ Page
const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: "How far in advance should I book?",
      a: "We recommend booking at least 2-4 weeks in advance for regular events, and 2-3 months for large events like weddings. Popular dates fill up quickly, especially during peak season (May-September)."
    },
    {
      q: "Do you deliver and set up the equipment?",
      a: "Yes! Delivery, setup, and pickup are included in our service. We'll work with you to schedule delivery before your event and pickup after."
    },
    {
      q: "What areas do you serve?",
      a: "We serve Philadelphia, Delaware County (including Ardmore, Media, Wayne), and surrounding areas including parts of Montgomery and Chester Counties. Delivery fees vary based on location."
    },
    {
      q: "What is your cancellation policy?",
      a: "Cancellations made more than 7 days before the event receive a full refund. Cancellations within 7 days may be subject to a 50% charge. Weather-related cancellations are handled on a case-by-case basis."
    },
    {
      q: "Do I need to clean the equipment before pickup?",
      a: "No, you don't need to clean the equipment. Simply ensure it's ready for pickup at the scheduled time. Please avoid leaving food or debris on items."
    },
    {
      q: "What happens if something gets damaged?",
      a: "Minor wear and tear is expected and included. For significant damage, you may be responsible for repair or replacement costs. We recommend discussing this during booking."
    },
    {
      q: "How do the discounts work?",
      a: "Orders over $350 automatically receive 10% off non-package items. You can also use promo code SUMMER10. One discount per customer. Tent packages are already discounted."
    },
    {
      q: "Can I make changes to my order after booking?",
      a: "Yes, you can modify your order up to 48 hours before delivery, subject to availability. Contact us as soon as possible for any changes."
    }
  ];

  return (
    <main className="pt-28 md:pt-36" data-testid="faq-page">
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-serif text-4xl md:text-5xl mb-4">Frequently Asked Questions</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about our party rental services.
          </p>
        </div>
      </section>

      <section className="section-padding">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div 
                key={faq.q} 
                className="bg-white rounded-xl border border-gray-100 overflow-hidden"
                data-testid={`faq-${faq.q.slice(0,20)}`}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === faq.q ? null : faq.q)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-lg pr-4">{faq.q}</span>
                  <ChevronRight className={`w-5 h-5 text-celebration-red flex-shrink-0 transition-transform ${openIndex === faq.q ? 'rotate-90' : ''}`} />
                </button>
                {openIndex === faq.q && (
                  <div className="px-6 pb-5 text-gray-600 border-t">
                    <p className="pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 text-center p-8 bg-gray-50 rounded-2xl">
            <h3 className="font-serif text-2xl mb-4">Still Have Questions?</h3>
            <p className="text-gray-600 mb-6">
              Our team is here to help! Contact us anytime.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={`tel:${BUSINESS.phone}`} className="btn-primary">
                <Phone className="w-4 h-4 inline mr-2" />
                {BUSINESS.phone}
              </a>
              <Link to="/contact" className="btn-secondary">
                Send a Message
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-28 md:pt-36" data-testid="contact-page">
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-serif text-4xl md:text-5xl mb-4">Contact Us</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Ready to plan your event? Get in touch with us today!
          </p>
        </div>
      </section>

      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-serif text-3xl mb-8">Get In Touch</h2>
              
              <div className="space-y-6">
                <a 
                  href={`tel:${BUSINESS.phone}`}
                  className="flex items-start gap-4 p-6 bg-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow"
                  data-testid="contact-phone"
                >
                  <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-celebration-red" />
                  </div>
                  <div>
                    <h3 className="font-medium text-lg">Phone</h3>
                    <p className="text-celebration-red font-bold text-xl">{BUSINESS.phone}</p>
                    <p className="text-gray-500 text-sm">Call or text anytime</p>
                  </div>
                </a>

                <a 
                  href={`mailto:${BUSINESS.email}`}
                  className="flex items-start gap-4 p-6 bg-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-celebration-red" />
                  </div>
                  <div>
                    <h3 className="font-medium text-lg">Email</h3>
                    <p className="text-celebration-red font-bold">{BUSINESS.email}</p>
                    <p className="text-gray-500 text-sm">We respond within 24 hours</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-6 bg-white rounded-xl border border-gray-100">
                  <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-celebration-red" />
                  </div>
                  <div>
                    <h3 className="font-medium text-lg">Service Area</h3>
                    <p className="text-gray-700 font-medium">{BUSINESS.location}</p>
                    <p className="text-gray-500 text-sm">Delivery fees vary by location</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-white rounded-xl border border-gray-100">
                  <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-celebration-red" />
                  </div>
                  <div>
                    <h3 className="font-medium text-lg">Hours</h3>
                    <p className="text-gray-700">Mon - Sat: 8am - 8pm</p>
                    <p className="text-gray-700">Sunday: 10am - 6pm</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
              <h2 className="font-serif text-2xl mb-6">Send Us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full p-4 border rounded-lg bg-gray-50"
                    data-testid="contact-form-name"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full p-4 border rounded-lg bg-gray-50"
                      data-testid="contact-form-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full p-4 border rounded-lg bg-gray-50"
                      data-testid="contact-form-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                  <select
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full p-4 border rounded-lg bg-gray-50"
                    data-testid="contact-form-subject"
                  >
                    <option value="">Select a subject</option>
                    <option value="quote">Request a Quote</option>
                    <option value="availability">Check Availability</option>
                    <option value="products">Product Questions</option>
                    <option value="general">General Inquiry</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full p-4 border rounded-lg bg-gray-50"
                    rows={5}
                    data-testid="contact-form-message"
                  />
                </div>

                <button 
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary flex items-center justify-center gap-2"
                  data-testid="contact-form-submit"
                >
                  {loading ? 'Sending...' : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

import { AdminLoginForm } from "./components/AdminLoginForm";
import { AdminStats } from "./components/AdminStats";
import { OrderDetailModal } from "./components/OrderDetailModal";
import { ProductCard } from "./components/ProductCard";
import { CategoryFilter, PRODUCT_CATEGORIES } from "./components/CategoryFilter";

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const handleLogin = async (pin) => {
    try {
      await axios.post(`${API}/admin/login`, { pin });
      setIsAuthenticated(true);
      fetchOrders();
      fetchStats();
      return null;
    } catch {
      return 'Invalid PIN. Please try again.';
    }
  };

  const fetchOrders = async (statusFilter) => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API}/admin/orders${params}`);
      setOrders(res.data);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`);
      setStats(res.data);
    } catch {
      toast.error('Failed to load stats');
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/status`, { status: newStatus });
      toast.success(`Order marked as ${newStatus}`);
      fetchOrders(filter);
      fetchStats();
    } catch {
      toast.error('Failed to update order');
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`);
      toast.success('Order deleted');
      fetchOrders(filter);
      fetchStats();
      setSelectedOrder(null);
    } catch {
      toast.error('Failed to delete order');
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    fetchOrders(newFilter);
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  if (!isAuthenticated) {
    return <AdminLoginForm onLogin={handleLogin} />;
  }

  const renderOrdersContent = () => {
    if (loading) {
      return (
        <div className="text-center py-20">
          <div className="w-10 h-10 border-4 border-celebration-red border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      );
    }
    if (orders.length === 0) {
      return (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No orders found</p>
          <p className="text-gray-400 text-sm mt-1">Orders will appear here when customers place them</p>
        </div>
      );
    }
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full" data-testid="orders-table">
            <thead>
              <tr className="bg-gray-50 text-left text-sm text-gray-500">
                <th className="px-6 py-4 font-medium">Customer</th>
                <th className="px-6 py-4 font-medium">Items</th>
                <th className="px-6 py-4 font-medium">Event Date</th>
                <th className="px-6 py-4 font-medium">Total</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orders.map(order => (
                <tr key={order.id} className="hover:bg-gray-50 transition-colors" data-testid={`order-row-${order.id}`}>
                  <td className="px-6 py-4">
                    <p className="font-medium">{order.customer_name}</p>
                    <p className="text-sm text-gray-500">{order.email}</p>
                    <p className="text-sm text-gray-400">{order.phone}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      {order.items.slice(0, 2).map((item) => (
                        <p key={item.product_name} className="text-sm">{item.quantity}x {item.product_name}</p>
                      ))}
                      {order.items.length > 2 && <p className="text-xs text-gray-400">+{order.items.length - 2} more</p>}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">{order.event_date}</td>
                  <td className="px-6 py-4 font-bold text-celebration-red">${order.total.toFixed(2)}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[order.status] || 'bg-gray-100 text-gray-600'}`}>{order.status}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSelectedOrder(order)} className="p-2 hover:bg-gray-100 rounded-lg" title="View Details" data-testid={`view-order-${order.id}`}>
                        <Eye className="w-4 h-4 text-gray-600" />
                      </button>
                      <div className="relative group">
                        <button className="p-2 hover:bg-gray-100 rounded-lg" title="Update Status"><ChevronDown className="w-4 h-4 text-gray-600" /></button>
                        <div className="absolute right-0 top-full mt-1 bg-white shadow-lg rounded-lg border py-1 hidden group-hover:block z-10 min-w-[140px]">
                          {['pending', 'confirmed', 'completed', 'cancelled'].map(s => (
                            <button key={s} onClick={() => updateStatus(order.id, s)} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 capitalize" data-testid={`set-status-${s}-${order.id}`}>{s}</button>
                          ))}
                        </div>
                      </div>
                      <button onClick={() => deleteOrder(order.id)} className="p-2 hover:bg-red-50 rounded-lg" title="Delete" data-testid={`delete-order-${order.id}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-gray-100">
          {orders.map(order => (
            <div key={order.id} className="p-4" data-testid={`order-card-${order.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-medium">{order.customer_name}</p>
                  <p className="text-sm text-gray-500">{order.email}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[order.status] || 'bg-gray-100'}`}>{order.status}</span>
              </div>
              <div className="text-sm text-gray-600 mb-2">
                {order.items.map((item) => (
                  <span key={item.product_name}>{order.items.indexOf(item) > 0 ? ', ' : ''}{item.quantity}x {item.product_name}</span>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <span className="font-bold text-celebration-red">${order.total.toFixed(2)}</span>
                <div className="flex gap-2">
                  <button onClick={() => setSelectedOrder(order)} className="text-sm text-blue-600 font-medium">View</button>
                  <button onClick={() => deleteOrder(order.id)} className="text-sm text-red-500 font-medium">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <main className="pt-28 md:pt-36 min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl">Orders Dashboard</h1>
            <p className="text-gray-500 mt-1">Manage your incoming orders</p>
          </div>
          <button onClick={() => setIsAuthenticated(false)} className="flex items-center gap-2 text-gray-500 hover:text-celebration-red transition-colors" data-testid="admin-logout">
            <LogOut className="w-5 h-5" /><span className="hidden sm:inline">Logout</span>
          </button>
        </div>

        <AdminStats stats={stats} />

        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(s => (
            <button key={s} onClick={() => handleFilterChange(s)} className={`px-5 py-2 rounded-full font-medium whitespace-nowrap transition-colors text-sm ${filter === s ? 'bg-celebration-red text-white' : 'bg-white text-gray-600 border hover:bg-gray-50'}`} data-testid={`filter-${s}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {renderOrdersContent()}

        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdateStatus={updateStatus}
          onDelete={deleteOrder}
        />
      </div>
    </main>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Main App
function App() {
  return (
    <CartProvider>
      <div className="App min-h-screen flex flex-col">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <ScrollToTop />
          <Navigation />
          <PromoBanner />
          <CartSidebar />
          <div className="flex-1">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/admin" element={<AdminPage />} />
            </Routes>
          </div>
          <Footer />
        </BrowserRouter>
      </div>
    </CartProvider>
  );
}

export default App;
